import { useRef } from 'react'
import * as PropTypes from 'prop-types'
import * as Yup from 'yup'
import moment from 'moment-timezone'
import { Formik } from 'formik'
import AsyncSelect from 'react-select/async'
import { useTranslation } from 'react-i18next'

import { getApiErrorMessage } from '@/utils'

import { formValidation } from '../../FormLegacy/validation'
import TextField from '../../FormLegacy/FormFieldsV2/TextField'
import API from '../../../api/api'
import { useSnackbar } from '../../../helpers/hooks/useSnackbar'

import { customStyles, DropdownIndicator } from './helpers'

const AddGuestForm = ({
  handleCancel,
  residents,
  building,
  refreshDataActionLogs,
  loadResidentOptions,
}) => {
  const { t } = useTranslation(['action-log', 'common'])
  const { displaySnackbar } = useSnackbar()
  const ref = useRef(null)

  const validationSchema = Yup.object().shape({
    name: formValidation.required,
    lastName: formValidation.required,
    company: formValidation.required,
    residentData: formValidation.required,
  })

  const handleFormSubmit = async ({ name, lastName, company, residentData }, { setSubmitting }) => {
    const dataToSend = {
      name: `${name} ${lastName}`,
      description: company,
      category: 'checkIn',
      date: building
        ? moment(new Date(Date.now())).tz(building.timezone, true).valueOf()
        : moment(new Date(Date.now())).valueOf(),
      clientId: residentData ? residentData.value : null,
      buildingId: building ? building.id : null,
    }

    try {
      await API.addGuest(dataToSend)
      setSubmitting(false)
      refreshDataActionLogs()
      handleCancel()
    } catch (err) {
      setSubmitting(false)
      displaySnackbar(getApiErrorMessage(err), true)
    }
  }

  return (
    <div className="add-guest-note">
      <Formik
        enableReinitialize={true}
        initialValues={{
          name: '',
          lastName: '',
          company: '',
          residentData: null,
        }}
        validationSchema={validationSchema}
        onSubmit={handleFormSubmit}
      >
        {({
          handleChange,
          handleSubmit,
          values,
          errors,
          touched,
          setFieldTouched,
          setFieldValue,
          isSubmitting,
          isValid,
          dirty,
        }) => {
          return (
            <div className="add-guest-note__container" ref={ref}>
              <div className="title">{t('action-log:addGuestForm.title')}</div>
              <div className="columns">
                <div className="column is-12">
                  <div className="resident-picker-container">
                    <label>{t('action-log:shared.residentLabel')}</label>
                    <AsyncSelect
                      loadOptions={loadResidentOptions}
                      defaultOptions={residents}
                      styles={customStyles}
                      components={{ DropdownIndicator }}
                      value={values.residentData}
                      defaultInputValue=""
                      placeholder={t('action-log:shared.searchResidentPlaceholder')}
                      isClearable
                      onChange={(residentData) => setFieldValue('residentData', residentData)}
                    />
                  </div>
                </div>
              </div>
              <div className="columns">
                <div className="column is-12">
                  <TextField
                    maxLength="100"
                    name={'name'}
                    label={t('action-log:addGuestForm.firstNameLabel')}
                    placeholder={t('action-log:addGuestForm.firstNamePlaceholder')}
                    onChange={handleChange}
                    value={values.name}
                    isError={touched.name && !!errors.name}
                    onBlur={() => setFieldTouched('name')}
                  />
                </div>
              </div>
              <div className="columns">
                <div className="column is-12">
                  <TextField
                    maxLength="100"
                    name={'lastName'}
                    label={t('action-log:addGuestForm.lastNameLabel')}
                    placeholder={t('action-log:addGuestForm.lastNamePlaceholder')}
                    onChange={handleChange}
                    value={values.lastName}
                    isError={touched.lastName && !!errors.lastName}
                    onBlur={() => setFieldTouched('lastName')}
                  />
                </div>
              </div>
              <div className="columns">
                <div className="column is-12">
                  <TextField
                    maxLength="100"
                    name={'company'}
                    label={t('action-log:addGuestForm.companyLabel')}
                    placeholder={t('action-log:addGuestForm.companyPlaceholder')}
                    onChange={handleChange}
                    value={values.company}
                    isError={touched.company && !!errors.company}
                    onBlur={() => setFieldTouched('company')}
                  />
                </div>
              </div>
              <div className="columns">
                <div className={'column is-6'}>
                  <button
                    type="submit"
                    className={`button button-gray full-width ${isSubmitting ? 'is-loading' : ''}`}
                    onClick={handleCancel}
                  >
                    {t('common:actions.cancel')}
                  </button>
                </div>
                <div className={'column is-6'}>
                  <button
                    type="submit"
                    className={`button button-primary full-width ${
                      isSubmitting ? 'is-loading' : ''
                    }`}
                    onClick={handleSubmit}
                    disabled={!isValid || !dirty || isSubmitting}
                  >
                    {t('action-log:addGuestForm.submitButton')}
                  </button>
                </div>
              </div>
            </div>
          )
        }}
      </Formik>
    </div>
  )
}

AddGuestForm.propTypes = {
  handleCancel: PropTypes.func,
  residents: PropTypes.array,
  staffPersons: PropTypes.array,
  currentUser: PropTypes.object,
  loadResidentOptions: PropTypes.func,
  building: PropTypes.object,
  refreshDataActionLogs: PropTypes.func,
}

export default AddGuestForm
